import { GoogleMap, Autocomplete, LoadScript, Marker } from '@react-google-maps/api';
import React, { useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CameraForm } from './CameraForm';
import { getTimezone } from '../../api/timezone';
import { Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import timezones from 'compact-timezone-list';
import USelect from '../../utils/USelect';
import UTextField2 from '../../utils/UTextField2';

type Libraries = ('drawing' | 'geometry' | 'localContext' | 'places' | 'visualization')[];
const libraries: Libraries = ['places'];

export default function LocationComponent({ display }: { display: boolean }) {
  const containerStyle = {
    width: '100%',
    height: '400px',
  };

  const { control, setValue } = useFormContext<CameraForm>();

  const latitude = useWatch({ name: 'latitude', control });
  const longitude = useWatch({ name: 'longitude', control });
  const center = {
    lat: parseFloat(latitude) || -3.745,
    lng: parseFloat(longitude) || -38.523,
  };

  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = function (_autocomplete: google.maps.places.Autocomplete): void {
    autocomplete.current = _autocomplete;
  };

  const setPosition = function ({ lat, lng }: { lat: number | undefined; lng: number | undefined }) {
    if (!lat || !lng) return;
    setValue('latitude', lat.toString());
    setValue('longitude', lng.toString());
    if (lat && lng)
      getTimezone({ lat, lng })
        .then((timezoneId) => {
          setValue('timezone', timezoneId);
        })
        .catch((e) => {
          console.error(e);
        });
  };

  const onPlaceChanged = function () {
    if (autocomplete.current !== null) {
      const place = autocomplete.current.getPlace();
      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      setPosition({ lat, lng });
    } else {
      console.error('Autocomplete is not loaded yet');
    }
  };

  const onDragEnd = function (e: google.maps.MapMouseEvent) {
    setPosition({ lat: e.latLng?.lat(), lng: e.latLng?.lng() });
  };

  const onDblClick = function (e: google.maps.MapMouseEvent) {
    setPosition({ lat: e.latLng?.lat(), lng: e.latLng?.lng() });
  };

  return (
    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_KEY || ''} libraries={libraries}>
      {display && (
        <>
          <Grid item xs={12}>
            <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={10} onDblClick={onDblClick}>
              <Marker draggable={true} position={center} onDragEnd={onDragEnd}></Marker>
            </GoogleMap>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
              <TextField label="Search Location" fullWidth />
            </Autocomplete>
          </Grid>
          <Grid item xs={6}>
            <UTextField2
              name="latitude"
              options={{
                valueAsNumber: true,
                required: 'Latitude is required',
                min: { value: -90, message: 'Should be greater than -90' },
                max: { value: 90, message: 'Should be lower than 90' },
              }}
              fullWidth
              label="Latitude"
              endAdornment={<InputAdornment position="end">&deg;</InputAdornment>}
              defaultValue="0"
              required={true}
              shrink={true}
            />
          </Grid>
          <Grid item xs={6}>
            <UTextField2
              name="longitude"
              options={{
                valueAsNumber: true,
                required: 'Longitude is required',
                min: { value: -180, message: 'Should be greater than -180' },
                max: { value: 180, message: 'Should be lower than 180' },
              }}
              fullWidth
              label="Longitude"
              endAdornment={<InputAdornment position="end">&deg;</InputAdornment>}
              defaultValue="0"
              required={true}
              shrink={true}
            />
          </Grid>
          <Grid item xs={12}>
            <USelect name="timezone" fullWidth label="Timezone">
              {timezones.map((timezone: { tzCode: string; label: string }) => {
                return (
                  <MenuItem value={timezone.tzCode} key={timezone.tzCode}>
                    {timezone.label}
                  </MenuItem>
                );
              })}
            </USelect>
          </Grid>
        </>
      )}
    </LoadScript>
  );
}
